// Armazens.js - CORRIGIDO: REFRESH COM PRODUTOS ATUALIZADOS + DETECÇÃO DE ATIVAÇÃO + EXPORTAÇÃO (CSV, EXCEL E PDF)
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Armazens.css';
import { Icon } from './SharedComponents';

const API_BASE_URL = '/api';

// ==============================================
// CONFIGURAÇÕES PADRÃO (fallback)
// ==============================================
const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.'
    },
    formatoData: 'DD/MM/YYYY',
    timezone: 'Africa/Maputo',
    idioma: 'pt-PT',
    iva: 16
};

// ==============================================
// CONSTANTES
// ==============================================
const TIPOS_ARMAZEM = [
    { value: 'principal', label: 'Principal' },
    { value: 'secundario', label: 'Secundário' },
    { value: 'filial', label: 'Filial' },
    { value: 'deposito', label: 'Depósito' },
    { value: 'loja', label: 'Loja' }
];

const STATUS_ARMAZEM = [
    { value: 'ativo', label: 'Ativo', color: '#10b981' },
    { value: 'inativo', label: 'Inativo', color: '#dc2626' },
    { value: 'manutencao', label: 'Em Manutenção', color: '#f59e0b' }
];

const TIPO_TRANSFERENCIA = [
    { value: 'mover', label: 'Mover (remove do origem)' },
    { value: 'duplicar', label: 'Duplicar/Transferir (mantém no origem)' }
];

// ==============================================
// 🔥 FUNÇÃO PARA OBTER DATA/HORA LOCAL
// ==============================================
const obterDataHoraLocal = () => {
    const agora = new Date();
    const ano = agora.getFullYear();
    const mes = String(agora.getMonth() + 1).padStart(2, '0');
    const dia = String(agora.getDate()).padStart(2, '0');
    const horas = String(agora.getHours()).padStart(2, '0');
    const minutos = String(agora.getMinutes()).padStart(2, '0');
    return `${ano}-${mes}-${dia}T${horas}:${minutos}`;
};

// ==============================================
// COMPONENTE DE GRÁFICO
// ==============================================
const SimpleBarChart = ({ data, labels, colors, title, formatValue }) => {
    const maxValue = Math.max(...data, 1);

    return (
        <div className="simple-chart">
            {title && <h4 className="chart-title">{title}</h4>}
            <div className="chart-bars">
                {data.map((value, index) => {
                    const percentage = (value / maxValue) * 100;
                    const color = colors?.[index] || '#4f46e5';
                    const displayValue = formatValue ? formatValue(value) : value;
                    return (
                        <div key={index} className="chart-bar-wrapper">
                            <div className="chart-bar-label">{labels[index]}</div>
                            <div className="chart-bar-track">
                                <div
                                    className="chart-bar-fill"
                                    style={{
                                        width: `${Math.max(percentage, 5)}%`,
                                        backgroundColor: color
                                    }}
                                >
                                    <span className="chart-bar-value">{displayValue}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ==============================================
// COMPONENTE DE GAUGE
// ==============================================
const SimpleGauge = ({ value, max, label, color, formatValue }) => {
    const percentage = Math.min((value / max) * 100, 100);
    const circumference = 2 * Math.PI * 40;
    const offset = circumference - (percentage / 100) * circumference;
    const displayValue = formatValue ? formatValue(value) : value;

    return (
        <div className="simple-gauge">
            <svg viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke={color || '#4f46e5'}
                    strokeWidth="8"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    transform="rotate(-90 50 50)"
                />
                <text x="50" y="50" textAnchor="middle" dominantBaseline="central" className="gauge-value">
                    {displayValue}
                </text>
            </svg>
            <div className="gauge-label">{label}</div>
        </div>
    );
};

export default function Armazens() {
    // ==============================================
    // ESTADOS PRINCIPAIS
    // ==============================================
    const [armazens, setArmazens] = useState([]);
    const [filteredArmazens, setFilteredArmazens] = useState([]);
    const [allArmazens, setAllArmazens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [configEmpresa, setConfigEmpresa] = useState(CONFIG_PADRAO);

    // ==============================================
    // MODAIS
    // ==============================================
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showMovimentacaoModal, setShowMovimentacaoModal] = useState(false);
    const [armazemToDelete, setArmazemToDelete] = useState(null);
    const [armazemDetalhes, setArmazemDetalhes] = useState(null);
    const [armazemMovimentacao, setArmazemMovimentacao] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });

    // ==============================================
    // TABS DOS MODAIS
    // ==============================================
    const [activeTab, setActiveTab] = useState('geral');
    const [editActiveTab, setEditActiveTab] = useState('geral');
    const [detailsActiveTab, setDetailsActiveTab] = useState('dados');
    const [movimentacaoActiveTab, setMovimentacaoActiveTab] = useState('entrada');

    // ==============================================
    // PAGINAÇÃO
    // ==============================================
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    });

    // ==============================================
    // PAGINAÇÃO PARA HISTÓRICO
    // ==============================================
    const [historicoPagination, setHistoricoPagination] = useState({
        page: 1,
        limit: 6,
        total: 0,
        pages: 0
    });

    // ==============================================
    // ESTADOS DE USUÁRIO E EMPRESA
    // ==============================================
    const [isSuperadmin, setIsSuperadmin] = useState(false);
    const [loggedUser, setLoggedUser] = useState(null);
    const [empresaId, setEmpresaId] = useState(null);
    const [produtos, setProdutos] = useState([]);
    const [movimentacoes, setMovimentacoes] = useState([]);
    const [armazensDisponiveis, setArmazensDisponiveis] = useState([]);
    const [produtoSelecionado, setProdutoSelecionado] = useState(null);

    // ==============================================
    // LOADING STATES
    // ==============================================
    const [createLoading, setCreateLoading] = useState(false);
    const [createMessage, setCreateMessage] = useState({ text: '', type: '' });
    const [editLoading, setEditLoading] = useState(false);
    const [editMessage, setEditMessage] = useState({ text: '', type: '' });
    const [movimentacaoLoading, setMovimentacaoLoading] = useState(false);
    const [movimentacaoMessage, setMovimentacaoMessage] = useState({ text: '', type: '' });

    // ==============================================
    // ESTATÍSTICAS - PARA TODOS OS ARMAZÉNS
    // ==============================================
    const [stats, setStats] = useState({
        total: 0,
        ativos: 0,
        inativos: 0,
        manutencao: 0,
        capacidadeTotal: 0,
        ocupacaoTotal: 0
    });

    // ==============================================
    // FILTROS AVANÇADOS
    // ==============================================
    const [filtros, setFiltros] = useState({
        tipo: 'todos',
        status: 'todos',
        capacidade: 'todos'
    });
    const [showFiltros, setShowFiltros] = useState(false);

    // ==============================================
    // FORM DATA - CRIAÇÃO
    // ==============================================
    const [formData, setFormData] = useState({
        nome: '',
        codigo: '',
        tipo: 'principal',
        endereco: '',
        cidade: '',
        provincia: '',
        pais: 'Moçambique',
        capacidade: 0,
        temperatura: '',
        umidade: '',
        responsavel: '',
        telefone: '',
        email: '',
        observacoes: '',
        status: 'ativo',
        ativo: true
    });

    // ==============================================
    // FORM DATA - EDIÇÃO
    // ==============================================
    const [editFormData, setEditFormData] = useState(null);

    // ==============================================
    // FORM DATA - MOVIMENTAÇÃO (TRANSFERÊNCIA) - CORRIGIDO
    // ==============================================
    const [movimentacaoFormData, setMovimentacaoFormData] = useState({
        produtoId: '',
        quantidade: 0,
        tipoTransferencia: 'mover',
        armazemDestinoId: '',
        observacao: '',
        dataMovimentacao: obterDataHoraLocal()
    });

    // ==============================================
    // API INSTANCE
    // ==============================================
    const api = axios.create({
        baseURL: API_BASE_URL,
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache'
        }
    });

    // ==============================================
    // FUNÇÕES AUXILIARES
    // ==============================================
    const extrairEmpresaId = (empresa) => {
        if (!empresa) return null;
        if (typeof empresa === 'string') return empresa;
        if (typeof empresa === 'object' && empresa._id) return empresa._id;
        if (typeof empresa === 'object' && empresa.id) return empresa.id;
        return null;
    };

    const obterEmpresaIdDoUtilizador = (user) => {
        if (!user) return null;
        let id = extrairEmpresaId(user.empresaId);
        if (id) return id;
        id = extrairEmpresaId(user.empresa);
        if (id) return id;
        id = extrairEmpresaId(user.empresaAtual);
        if (id) return id;
        if (user.empresas && Array.isArray(user.empresas) && user.empresas.length > 0) {
            id = extrairEmpresaId(user.empresas[0]);
            if (id) return id;
        }
        return null;
    };

    const showTempToast = (title, message, type = 'success') => {
        setMessage({ text: `${title}: ${message}`, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    };

    // ==============================================
    // FORMATAÇÃO
    // ==============================================
    const formatarDataHora = (data) => {
        if (!data) return '-';
        const d = new Date(data);
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const ano = d.getFullYear();
        const horas = String(d.getHours()).padStart(2, '0');
        const minutos = String(d.getMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
    };

    const formatarMoeda = (valor) => {
        const moeda = configEmpresa?.moeda || CONFIG_PADRAO.moeda;
        const { simbolo = 'MT', casasDecimais = 2, separadorDecimal = ',', separadorMilhar = '.' } = moeda;

        if (valor === undefined || valor === null || isNaN(valor)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        let numero = Number(valor);
        if (isNaN(numero)) {
            return `0${separadorDecimal}${'0'.repeat(casasDecimais)} ${simbolo}`;
        }

        numero = numero.toFixed(casasDecimais);
        const partes = numero.split('.');
        let inteiros = partes[0];
        const decimais = partes[1] || '0'.repeat(casasDecimais);

        inteiros = inteiros.replace(/\B(?=(\d{3})+(?!\d))/g, separadorMilhar);

        return `${inteiros}${separadorDecimal}${decimais} ${simbolo}`;
    };

    const formatarNumero = (valor) => {
        if (valor === undefined || valor === null) return '0';
        const num = Number(valor);
        if (isNaN(num)) return '0';

        if (num >= 1e9) {
            return (num / 1e9).toFixed(1) + 'B';
        }
        if (num >= 1e6) {
            return (num / 1e6).toFixed(1) + 'M';
        }
        if (num >= 1e3) {
            return (num / 1e3).toFixed(1) + 'k';
        }

        return num.toLocaleString('pt-PT');
    };

    const formatarPercentagem = (valor) => {
        if (valor === undefined || valor === null) return '0%';
        return `${Number(valor).toFixed(1)}%`;
    };

    // ==============================================
    // BADGES E ÍCONES
    // ==============================================
    const getStatusArmazem = (status) => {
        const found = STATUS_ARMAZEM.find(s => s.value === status);
        if (!found) return { label: status || 'Desconhecido', color: '#6b7280', icon: 'help' };
        return {
            label: found.label,
            color: found.color,
            icon: status === 'ativo' ? 'check_circle' : status === 'inativo' ? 'block' : 'build'
        };
    };

    const getTipoLabel = (tipo) => {
        const found = TIPOS_ARMAZEM.find(t => t.value === tipo);
        return found ? found.label : tipo || '-';
    };

    const getCapacidadeStatus = (ocupacao) => {
        if (ocupacao === 0) return { label: 'Vazio', color: '#6b7280', icon: 'help' };
        if (ocupacao >= 90) return { label: 'Lotado', color: '#dc2626', icon: 'warning' };
        if (ocupacao >= 70) return { label: 'Quase Cheio', color: '#f59e0b', icon: 'info' };
        if (ocupacao >= 40) return { label: 'Moderado', color: '#3b82f6', icon: 'info' };
        return { label: 'Bastante Espaço', color: '#10b981', icon: 'check_circle' };
    };

    // ==============================================
    // 🔥 FUNÇÃO PARA CALCULAR OCUPAÇÃO BASEADA NO STOCK TOTAL
    // ==============================================
    const calcularOcupacao = (armazem) => {
        if (!armazem) return 0;
        const capacidade = armazem.capacidade || 0;
        const stockTotal = armazem.stockTotal || 0;

        if (capacidade === 0) return 0;
        return Math.min((stockTotal / capacidade) * 100, 100);
    };

    // ==============================================
    // 🔥 CALCULAR PRODUTOS POR ARMAZÉM (FALLBACK - USANDO PRODUTOS)
    // ==============================================
    const calcularProdutosPorArmazemFallback = (armazensData, produtosData) => {
        if (!armazensData || !Array.isArray(armazensData)) return [];
        if (!produtosData || !Array.isArray(produtosData)) return armazensData;

        const contagem = {};
        const stock = {};

        produtosData.forEach(produto => {
            if (produto && produto.armazemId) {
                const armazemId = typeof produto.armazemId === 'object'
                    ? produto.armazemId._id
                    : produto.armazemId;
                if (armazemId) {
                    contagem[armazemId] = (contagem[armazemId] || 0) + 1;
                    stock[armazemId] = (stock[armazemId] || 0) + (produto.stockAtual || 0);
                }
            }
        });

        return armazensData.map(armazem => {
            if (!armazem) return null;
            const armazemId = armazem._id || armazem.id;
            const totalProdutos = contagem[armazemId] || 0;
            const stockTotal = stock[armazemId] || 0;

            const ocupacao = calcularOcupacao({ ...armazem, stockTotal });

            return {
                ...armazem,
                totalProdutos: armazem.totalProdutos || totalProdutos,
                stockTotal: armazem.stockTotal || stockTotal,
                ocupacao: ocupacao
            };
        }).filter(a => a !== null);
    };

    // ==============================================
    // 🔥 CALCULAR PRODUTOS E STOCK POR ARMAZÉM - CORRIGIDO
    // ==============================================
    const calcularProdutosPorArmazem = (armazensData, produtosData = []) => {
        if (!armazensData || !Array.isArray(armazensData)) return [];

        return armazensData.map(armazem => {
            if (!armazem) return null;

            const saldoProdutos = {};
            let stockTotal = 0;

            const movimentacoes = armazem.movimentacoes || [];

            // 🔥 PRIMEIRO: Calcular usando as movimentações normais (entrada/saída)
            for (const mov of movimentacoes) {
                if (!mov) continue;

                const produtoId = mov.produtoId ?
                    (typeof mov.produtoId === 'object' ? mov.produtoId._id : mov.produtoId) :
                    'unknown';

                // 🔥 IGNORAR movimentações de duplicação para cálculo de stock
                if (mov.tipoTransferencia === 'duplicar') {
                    continue;
                }

                if (!saldoProdutos[produtoId]) {
                    saldoProdutos[produtoId] = {
                        produtoId: mov.produtoId,
                        produtoNome: mov.produtoNome || 'Produto',
                        produtoCodigo: mov.produtoCodigo || '',
                        quantidade: 0,
                        precoUnitario: mov.precoUnitario || 0
                    };
                }

                if (mov.tipo === 'entrada' || mov.tipo === 'transferencia_entrada') {
                    saldoProdutos[produtoId].quantidade += mov.quantidade || 0;
                    stockTotal += mov.quantidade || 0;
                } else if (mov.tipo === 'saida' || mov.tipo === 'transferencia_saida') {
                    saldoProdutos[produtoId].quantidade -= mov.quantidade || 0;
                    stockTotal -= mov.quantidade || 0;
                }
            }

            // 🔥 SEGUNDO: Buscar produtos associados ao armazém (usando produtosData recebido)
            let stockReal = 0;
            const produtosNoArmazem = [];
            let totalProdutosNoArmazem = 0;

            // 🔥 CORREÇÃO: Usar produtosData em vez do estado produtos
            if (produtosData && produtosData.length > 0) {
                const produtosFiltrados = produtosData.filter(p => {
                    const armazemId = typeof p.armazemId === 'object' ? p.armazemId?._id : p.armazemId;
                    return armazemId === armazem._id;
                });

                produtosFiltrados.forEach(p => {
                    const quantidade = p.stockAtual || 0;
                    stockReal += quantidade;
                    totalProdutosNoArmazem++;
                    produtosNoArmazem.push({
                        produtoId: p._id,
                        produtoNome: p.nome,
                        produtoCodigo: p.codigo,
                        quantidade: quantidade,
                        precoUnitario: p.precoVenda || 0
                    });
                });
            }

            // 🔥 CORREÇÃO: Usar o totalProdutosNoArmazem se tiver produtos, senão usar o calculado pelas movimentações
            const totalProdutosFinal = totalProdutosNoArmazem > 0
                ? totalProdutosNoArmazem
                : Object.values(saldoProdutos).filter(p => p && p.quantidade > 0).length;

            // Usar o stockReal se tiver produtos, senão usar o calculado pelas movimentações
            const stockFinal = stockReal > 0 ? stockReal : Math.max(0, stockTotal);

            // Produtos ativos: se tiver produtos no armazém, usar eles (mesmo com stock 0)
            const produtosAtivos = totalProdutosNoArmazem > 0
                ? produtosNoArmazem
                : Object.values(saldoProdutos).filter(p => p && p.quantidade > 0);

            return {
                ...armazem,
                totalProdutos: totalProdutosFinal,
                stockTotal: stockFinal,
                produtosNoArmazem: produtosAtivos,
                ocupacao: calcularOcupacao({ ...armazem, stockTotal: stockFinal })
            };
        }).filter(a => a !== null);
    };

    // ==============================================
    // 🔥 CALCULAR ESTATÍSTICAS PARA TODOS OS ARMAZÉNS (SOMA DAS OCUPAÇÕES)
    // ==============================================
    const calcularStatsTodos = (armazensData) => {
        const data = Array.isArray(armazensData) ? armazensData.filter(a => a !== null) : [];

        const somaOcupacao = data.reduce((sum, a) => sum + (a?.ocupacao || 0), 0);

        const newStats = {
            total: data.length,
            ativos: data.filter(a => a && a.status === 'ativo').length,
            inativos: data.filter(a => a && a.status === 'inativo').length,
            manutencao: data.filter(a => a && a.status === 'manutencao').length,
            capacidadeTotal: data.reduce((sum, a) => sum + (a?.capacidade || 0), 0),
            ocupacaoTotal: somaOcupacao
        };
        setStats(newStats);
    };

    // ==============================================
    // ORDENAR ARMAZÉNS (MAIS RECENTES PRIMEIRO)
    // ==============================================
    const ordenarArmazens = (lista) => {
        if (!lista || !Array.isArray(lista)) return [];
        return [...lista].filter(a => a !== null).sort((a, b) => {
            const dataA = a?.createdAt ? new Date(a.createdAt) : new Date(0);
            const dataB = b?.createdAt ? new Date(b.createdAt) : new Date(0);
            return dataB.getTime() - dataA.getTime();
        });
    };

    // ==============================================
    // 🔥 VERIFICAR SE ARMAZÉM TEM PRODUTOS ASSOCIADOS
    // ==============================================
    const armazemTemProdutos = (armazem) => {
        if (!armazem) return false;
        if (armazem.totalProdutos !== undefined) {
            return armazem.totalProdutos > 0;
        }
        return produtos.some(p => {
            const armazemId = typeof p.armazemId === 'object' ? p.armazemId?._id : p.armazemId;
            return armazemId === armazem._id && (p.stockAtual || 0) > 0;
        });
    };

    // ==============================================
    // 🔥 ABRIR MODAL DE EXCLUSÃO (COM VALIDAÇÃO)
    // ==============================================
    const openDeleteModal = (armazem) => {
        if (armazemTemProdutos(armazem)) {
            showTempToast(
                'Ação bloqueada',
                `Não é possível excluir o armazém "${armazem.nome}" pois ele possui ${armazem.totalProdutos || 0} produto(s) associado(s). Transfira ou remova os produtos primeiro.`,
                'danger'
            );
            return;
        }
        setArmazemToDelete(armazem);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setArmazemToDelete(null);
    };

    // ==============================================
    // 🔥 FUNÇÃO DE REFRESH (CORRIGIDO)
    // ==============================================
    const refreshArmazens = async () => {
        console.log('🔄 Atualizando armazéns...');
        try {
            // 🔥 Buscar produtos atualizados
            const produtosAtualizados = await fetchProdutos();
            // 🔥 Atualizar o estado produtos
            setProdutos(produtosAtualizados);
            // 🔥 Buscar armazéns com os produtos atualizados
            await fetchArmazens(produtosAtualizados);
            console.log('✅ Armazéns atualizados com sucesso!');
        } catch (error) {
            console.error('❌ Erro ao atualizar armazéns:', error);
        }
    };

    // ==============================================
    // 🔥 BUSCAR ARMAZÉNS - CORRIGIDO
    // ==============================================
    const fetchArmazens = async (produtosData = produtos) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }

            const response = await api.get('/armazens');

            let armazensData = [];
            let config = { ...CONFIG_PADRAO };

            if (response.data) {
                if (response.data.data && Array.isArray(response.data.data)) {
                    armazensData = response.data.data;
                    if (response.data.configuracao) {
                        config = {
                            ...CONFIG_PADRAO,
                            ...response.data.configuracao,
                            moeda: {
                                ...CONFIG_PADRAO.moeda,
                                ...(response.data.configuracao.moeda || {})
                            }
                        };
                    }
                } else if (Array.isArray(response.data)) {
                    armazensData = response.data;
                }
            }

            if (!config.moeda) {
                config.moeda = { ...CONFIG_PADRAO.moeda };
            }

            setConfigEmpresa(config);

            // 🔥 CORREÇÃO: Passar produtosData para calcularProdutosPorArmazem
            const armazensComMovimentacoes = calcularProdutosPorArmazem(armazensData, produtosData);
            const temDadosViaMov = armazensComMovimentacoes.some(a => a && a.stockTotal > 0);

            let armazensFinais = armazensComMovimentacoes;

            if (!temDadosViaMov && produtosData && produtosData.length > 0) {
                console.log('⚠️ Nenhum stock via movimentações, usando fallback com produtos...');
                console.log('📦 Produtos disponíveis para fallback:', produtosData.length);
                armazensFinais = calcularProdutosPorArmazemFallback(armazensComMovimentacoes, produtosData);
            }

            const armazensOrdenados = ordenarArmazens(armazensFinais);
            setAllArmazens(armazensOrdenados);

            calcularStatsTodos(armazensOrdenados);

            let filtrados = [...armazensOrdenados];

            if (searchTerm && searchTerm.trim() !== '') {
                const term = searchTerm.toLowerCase().trim();
                filtrados = filtrados.filter(a => {
                    if (!a) return false;
                    const nome = a.nome || '';
                    const codigo = a.codigo || '';
                    const endereco = a.endereco || '';
                    const cidade = a.cidade || '';
                    const responsavel = a.responsavel || '';

                    return nome.toLowerCase().includes(term) ||
                        codigo.toLowerCase().includes(term) ||
                        endereco.toLowerCase().includes(term) ||
                        cidade.toLowerCase().includes(term) ||
                        responsavel.toLowerCase().includes(term);
                });
            }

            if (filtros.tipo && filtros.tipo !== 'todos') {
                filtrados = filtrados.filter(a => a && a.tipo === filtros.tipo);
            }

            if (filtros.status && filtros.status !== 'todos') {
                filtrados = filtrados.filter(a => a && a.status === filtros.status);
            }

            if (filtros.capacidade && filtros.capacidade !== 'todos') {
                filtrados = filtrados.filter(a => {
                    if (!a) return false;
                    const ocupacao = a.ocupacao || 0;
                    if (filtros.capacidade === 'cheio') {
                        return ocupacao >= 90;
                    } else if (filtros.capacidade === 'quase_cheio') {
                        return ocupacao >= 70 && ocupacao < 90;
                    } else if (filtros.capacidade === 'moderado') {
                        return ocupacao >= 40 && ocupacao < 70;
                    } else if (filtros.capacidade === 'livre') {
                        return ocupacao < 40;
                    }
                    return true;
                });
            }

            setArmazens(armazensOrdenados);
            setFilteredArmazens(filtrados);

            setPagination(prev => ({
                ...prev,
                total: filtrados.length,
                pages: Math.ceil(filtrados.length / prev.limit),
                page: 1
            }));

            console.log('✅ Armazéns atualizados na tabela:', filtrados.length);
            console.log('✅ Total de armazéns (todos):', armazensOrdenados.length);

        } catch (err) {
            console.error('❌ Erro ao carregar armazéns:', err);
            showTempToast('Erro', 'Não foi possível carregar os armazéns', 'danger');
            setConfigEmpresa(CONFIG_PADRAO);
        } finally {
            setLoading(false);
        }
    };

    // ==============================================
    // BUSCAR PRODUTOS
    // ==============================================
    const fetchProdutos = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.warn('⚠️ Token não encontrado');
                return [];
            }

            const response = await axios.get(`${API_BASE_URL}/produtos`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data) {
                const produtosData = Array.isArray(response.data) ? response.data : response.data.data || [];
                setProdutos(produtosData);
                console.log('✅ Produtos carregados:', produtosData.length);

                return produtosData;
            }
            return [];
        } catch (err) {
            console.error('❌ Erro ao carregar produtos:', err);
            throw err;
        }
    };

    // ==============================================
    // 🔥 BUSCAR MOVIMENTAÇÕES DO ARMAZÉM
    // ==============================================
    const fetchMovimentacoes = async (armazemId, page = 1, limit = 6) => {
        try {
            console.log(`📊 Buscando movimentações do armazém ${armazemId}, página ${page}, limite ${limit}`);
            const response = await api.get(`/armazens/${armazemId}/movimentacoes?page=${page}&limit=${limit}`);
            console.log('📊 Resposta da API:', response.data);

            if (response.data) {
                const dados = response.data.data || response.data || [];
                setMovimentacoes(dados);

                if (response.data.pagination) {
                    setHistoricoPagination({
                        page: response.data.pagination.page || 1,
                        limit: response.data.pagination.limit || 6,
                        total: response.data.pagination.total || 0,
                        pages: response.data.pagination.pages || 0
                    });
                } else {
                    setHistoricoPagination({
                        page: 1,
                        limit: 6,
                        total: dados.length,
                        pages: Math.ceil(dados.length / 6)
                    });
                }

                console.log(`✅ ${dados.length} movimentações carregadas`);
                return response.data;
            }
        } catch (err) {
            console.error('❌ Erro ao carregar movimentações:', err);
            setMovimentacoes([]);
            setHistoricoPagination({
                page: 1,
                limit: 6,
                total: 0,
                pages: 0
            });
            return null;
        }
    };

    const fetchLoggedUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && storedUser._id) {
                    setLoggedUser(storedUser);
                    setIsSuperadmin(storedUser.perfil === 'Superadmin');
                    const empresaIdExtraido = obterEmpresaIdDoUtilizador(storedUser);
                    setEmpresaId(empresaIdExtraido);
                    return storedUser;
                }
                return null;
            }

            const response = await axios.get(`/api/users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                const userData = response.data.data;
                setLoggedUser(userData);
                setIsSuperadmin(userData.perfil === 'Superadmin');
                const empresaIdExtraido = obterEmpresaIdDoUtilizador(userData);
                setEmpresaId(empresaIdExtraido);
                localStorage.setItem('usuario', JSON.stringify(userData));
                return userData;
            }
        } catch (err) {
            console.error('❌ Erro ao buscar utilizador:', err);
        }
        return null;
    };

    // ==============================================
    // FILTRAR ARMAZÉNS
    // ==============================================
    useEffect(() => {
        if (armazens.length > 0) {
            let resultados = [...armazens].filter(a => a !== null);

            if (searchTerm && searchTerm.trim() !== '') {
                const term = searchTerm.toLowerCase().trim();
                resultados = resultados.filter(a => {
                    if (!a) return false;
                    const nome = a.nome || '';
                    const codigo = a.codigo || '';
                    const endereco = a.endereco || '';
                    const cidade = a.cidade || '';
                    const responsavel = a.responsavel || '';

                    return nome.toLowerCase().includes(term) ||
                        codigo.toLowerCase().includes(term) ||
                        endereco.toLowerCase().includes(term) ||
                        cidade.toLowerCase().includes(term) ||
                        responsavel.toLowerCase().includes(term);
                });
            }

            if (filtros.tipo && filtros.tipo !== 'todos') {
                resultados = resultados.filter(a => a && a.tipo === filtros.tipo);
            }

            if (filtros.status && filtros.status !== 'todos') {
                resultados = resultados.filter(a => a && a.status === filtros.status);
            }

            if (filtros.capacidade && filtros.capacidade !== 'todos') {
                resultados = resultados.filter(a => {
                    if (!a) return false;
                    const ocupacao = a.ocupacao || 0;
                    if (filtros.capacidade === 'cheio') {
                        return ocupacao >= 90;
                    } else if (filtros.capacidade === 'quase_cheio') {
                        return ocupacao >= 70 && ocupacao < 90;
                    } else if (filtros.capacidade === 'moderado') {
                        return ocupacao >= 40 && ocupacao < 70;
                    } else if (filtros.capacidade === 'livre') {
                        return ocupacao < 40;
                    }
                    return true;
                });
            }

            resultados = ordenarArmazens(resultados);
            setFilteredArmazens(resultados);
            setPagination(prev => ({
                ...prev,
                total: resultados.length,
                pages: Math.ceil(resultados.length / prev.limit),
                page: 1
            }));
        }
    }, [searchTerm, armazens, filtros]);

    const limparFiltros = () => {
        setFiltros({
            tipo: 'todos',
            status: 'todos',
            capacidade: 'todos'
        });
        setSearchTerm('');
        setShowFiltros(false);
    };

    // ==============================================
    // 🔥 CARREGAR DADOS INICIAIS - CORRIGIDO
    // ==============================================
    useEffect(() => {
        const carregarDados = async () => {
            try {
                await fetchLoggedUser();
                console.log('📦 Iniciando carregamento de produtos...');
                const produtosCarregados = await fetchProdutos();
                console.log('✅ Produtos carregados:', produtosCarregados.length);

                // 🔥 Atualizar o estado produtos
                setProdutos(produtosCarregados);

                console.log('📦 Iniciando carregamento de armazéns com produtos...');
                // 🔥 CORREÇÃO: Usar refreshArmazens() que já trata a atualização
                await refreshArmazens();
                console.log('✅ Armazéns carregados com sucesso!');
                setLoading(false);
            } catch (error) {
                console.error('❌ Erro ao carregar dados iniciais:', error);
                setLoading(false);
            }
        };
        carregarDados();
    }, []);

    // ==============================================
    // 🔥 DETECTAR QUANDO A PÁGINA É ATIVADA - REFRESH AUTOMÁTICO
    // ==============================================
    useEffect(() => {
        let refreshing = false;

        const handleRefresh = async () => {
            if (refreshing) return;
            refreshing = true;
            try {
                console.log('🔄 Atualizando armazéns (página ativada)...');
                await refreshArmazens();
            } catch (error) {
                console.error('❌ Erro ao atualizar armazéns:', error);
            } finally {
                refreshing = false;
            }
        };

        // Quando a aba/tab do navegador é ativada
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('👁️ Página visível - atualizando...');
                handleRefresh();
            }
        };

        // Quando a página volta a ter foco
        const handleFocus = () => {
            console.log('🔄 Página com foco - atualizando...');
            handleRefresh();
        };

        // Adicionar listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        // Cleanup
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    // ==============================================
    // HANDLE CHANGE - CRIAÇÃO
    // ==============================================
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        setFormData(prev => ({ ...prev, [name]: val }));
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        setEditFormData(prev => ({ ...prev, [name]: val }));
    };

    // ==============================================
    // 🔥 HANDLE CHANGE - MOVIMENTAÇÃO
    // ==============================================
    const handleMovimentacaoChange = (e) => {
        const { name, value } = e.target;

        if (name === 'produtoId') {
            const produto = produtos.find(p => p._id === value);
            setProdutoSelecionado(produto || null);

            if (movimentacaoFormData.tipoTransferencia === 'mover' && produto) {
                setMovimentacaoFormData(prev => ({
                    ...prev,
                    [name]: value,
                    quantidade: produto.stockAtual || 0
                }));
            } else {
                setMovimentacaoFormData(prev => ({
                    ...prev,
                    [name]: value,
                    quantidade: 0
                }));
            }
            return;
        }

        if (name === 'tipoTransferencia') {
            const isMover = value === 'mover';
            if (isMover && movimentacaoFormData.produtoId) {
                const produto = produtos.find(p => p._id === movimentacaoFormData.produtoId);
                setMovimentacaoFormData(prev => ({
                    ...prev,
                    [name]: value,
                    quantidade: produto?.stockAtual || 0
                }));
            } else {
                setMovimentacaoFormData(prev => ({
                    ...prev,
                    [name]: value,
                    quantidade: 0
                }));
            }
            return;
        }

        setMovimentacaoFormData(prev => ({ ...prev, [name]: value }));
    };

    // ==============================================
    // FUNÇÕES DE PAGINAÇÃO
    // ==============================================
    const getCurrentPageArmazens = () => {
        const startIndex = (pagination.page - 1) * pagination.limit;
        const endIndex = startIndex + pagination.limit;
        return filteredArmazens.slice(startIndex, endIndex);
    };

    const goToPage = (page) => {
        if (page < 1 || page > pagination.pages) return;
        setPagination({ ...pagination, page });
    };

    const renderPagination = () => {
        const { page, pages, total } = pagination;
        if (pages <= 1) return null;

        const startItem = (page - 1) * pagination.limit + 1;
        const endItem = Math.min(page * pagination.limit, total);

        return (
            <div className="armazens-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} armazéns</span>
                </div>
                <div className="pagination-controls">
                    <button className="pagination-btn" onClick={() => goToPage(page - 1)} disabled={page === 1}>
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                            <button
                                key={p}
                                className={`pagination-number ${page === p ? 'active' : ''}`}
                                onClick={() => goToPage(p)}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                    <button className="pagination-btn" onClick={() => goToPage(page + 1)} disabled={page === pages}>
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // ==============================================
    // FUNÇÕES DE PAGINAÇÃO DO HISTÓRICO
    // ==============================================
    const goToHistoricoPage = async (page) => {
        if (page < 1 || page > historicoPagination.pages) return;
        if (armazemDetalhes) {
            await fetchMovimentacoes(armazemDetalhes._id, page, historicoPagination.limit);
        }
    };

    const renderHistoricoPagination = () => {
        const { page, pages, total, limit } = historicoPagination;
        if (pages <= 1) return null;

        const startItem = (page - 1) * limit + 1;
        const endItem = Math.min(page * limit, total);

        // 🔥 FUNÇÃO PARA GERAR OS NÚMEROS DAS PÁGINAS
        const renderPageNumbers = () => {
            const pageNumbers = [];
            const maxVisible = 2; // Número máximo de páginas visíveis
            const halfVisible = 1; // Quantas páginas antes/depois da atual

            // Se o total de páginas for menor ou igual ao máximo visível, mostra todas
            if (pages <= maxVisible) {
                for (let i = 1; i <= pages; i++) {
                    pageNumbers.push(i);
                }
                return pageNumbers;
            }

            // Sempre mostrar a primeira página
            pageNumbers.push(1);

            // Calcular o range de páginas ao redor da página atual
            let startPage = Math.max(2, page - halfVisible);
            let endPage = Math.min(pages - 1, page + halfVisible);

            // Ajustar se estiver próximo ao início
            if (page <= halfVisible + 1) {
                endPage = Math.min(pages - 1, halfVisible * 2 + 1);
            }

            // Ajustar se estiver próximo ao fim
            if (page >= pages - halfVisible) {
                startPage = Math.max(2, pages - halfVisible * 2);
            }

            // Adicionar reticências antes do meio se necessário
            if (startPage > 2) {
                pageNumbers.push('...');
            }

            // Adicionar as páginas do meio
            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
            }

            // Adicionar reticências depois do meio se necessário
            if (endPage < pages - 1) {
                pageNumbers.push('...');
            }

            // Sempre mostrar a última página
            if (pages > 1) {
                pageNumbers.push(pages);
            }

            return pageNumbers;
        };

        const pageNumbers = renderPageNumbers();

        return (
            <div className="historico-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} registos</span>
                </div>
                <div className="pagination-controls">
                    <button
                        className="pagination-btn"
                        onClick={() => goToHistoricoPage(page - 1)}
                        disabled={page === 1}
                    >
                        <Icon name="chevron_left" size={16} />
                    </button>
                    <div className="pagination-numbers">
                        {pageNumbers.map((p, index) => (
                            p === '...' ? (
                                <span key={`ellipsis-${index}`} className="pagination-ellipsis">…</span>
                            ) : (
                                <button
                                    key={p}
                                    className={`pagination-number ${page === p ? 'active' : ''}`}
                                    onClick={() => goToHistoricoPage(p)}
                                >
                                    {p}
                                </button>
                            )
                        ))}
                    </div>
                    <button
                        className="pagination-btn"
                        onClick={() => goToHistoricoPage(page + 1)}
                        disabled={page === pages}
                    >
                        <Icon name="chevron_right" size={16} />
                    </button>
                </div>
            </div>
        );
    };

    // ==============================================
    // RESET FORM
    // ==============================================
    const resetForm = () => {
        setFormData({
            nome: '',
            codigo: '',
            tipo: 'principal',
            endereco: '',
            cidade: '',
            provincia: '',
            pais: 'Moçambique',
            capacidade: 0,
            temperatura: '',
            umidade: '',
            responsavel: '',
            telefone: '',
            email: '',
            observacoes: '',
            status: 'ativo',
            ativo: true
        });
        setCreateMessage({ text: '', type: '' });
        setActiveTab('geral');
    };

    const resetEditForm = () => {
        setEditFormData(null);
        setEditMessage({ text: '', type: '' });
        setEditActiveTab('geral');
    };

    // ==============================================
    // RESET MOVIMENTAÇÃO - CORRIGIDO: DATA/HORA LOCAL
    // ==============================================
    const resetMovimentacaoForm = () => {
        setMovimentacaoFormData({
            produtoId: '',
            quantidade: 0,
            tipoTransferencia: 'mover',
            armazemDestinoId: '',
            observacao: '',
            dataMovimentacao: obterDataHoraLocal()
        });
        setMovimentacaoMessage({ text: '', type: '' });
        setMovimentacaoActiveTab('entrada');
        setArmazensDisponiveis([]);
        setProdutoSelecionado(null);
    };

    // ==============================================
    // CRIAÇÃO
    // ==============================================
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        setCreateMessage({ text: '', type: '' });

        if (!formData.nome.trim()) {
            setCreateMessage({ text: 'O nome do armazém é obrigatório', type: 'danger' });
            setCreateLoading(false);
            return;
        }

        let empresaParaEnviar = empresaId;
        if (!empresaParaEnviar && loggedUser) {
            empresaParaEnviar = obterEmpresaIdDoUtilizador(loggedUser);
        }

        if (!empresaParaEnviar) {
            setCreateMessage({
                text: 'Utilizador sem empresa associada. Contacte o administrador.',
                type: 'danger'
            });
            setCreateLoading(false);
            return;
        }

        const dadosParaEnviar = {
            ...formData,
            capacidade: parseInt(formData.capacidade) || 0,
            empresaId: empresaParaEnviar
        };

        try {
            const response = await api.post('/armazens', dadosParaEnviar);

            if (response.data) {
                setCreateMessage({ text: 'Armazém criado com sucesso!', type: 'success' });
                setTimeout(() => {
                    setShowCreateModal(false);
                    resetForm();
                    refreshArmazens();
                }, 1500);
            }
        } catch (err) {
            console.error('❌ Erro ao criar armazém:', err);
            setCreateMessage({
                text: err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao criar armazém',
                type: 'danger'
            });
        } finally {
            setCreateLoading(false);
        }
    };

    // ==============================================
    // 🔥 VER DETALHES
    // ==============================================
    const verDetalhes = async (armazem) => {
        try {
            setLoading(true);

            const response = await api.get(`/armazens/${armazem._id}`);
            const armazemCompleto = response.data.data || armazem;

            const dadosMov = calcularProdutosPorArmazem([armazemCompleto]);
            let armazemComDados = dadosMov && dadosMov[0] ? dadosMov[0] : armazemCompleto;

            if (armazemComDados && armazemComDados.totalProdutos === 0) {
                const dadosFallback = calcularProdutosPorArmazemFallback([armazemCompleto], produtos);
                armazemComDados = dadosFallback && dadosFallback[0] ? dadosFallback[0] : armazemComDados;
            }

            setArmazemDetalhes(armazemComDados);

            setMovimentacoes([]);
            await fetchMovimentacoes(armazem._id, 1, 6);

            setDetailsActiveTab('dados');
            setTimeout(() => {
                setShowDetailsModal(true);
                setLoading(false);
            }, 100);
        } catch (err) {
            console.error('❌ Erro ao buscar detalhes:', err);
            setArmazemDetalhes(armazem);
            setShowDetailsModal(true);
            setLoading(false);
        }
    };

    // ==============================================
    // 🔥 CARREGAR ARMAZÉM PARA EDIÇÃO
    // ==============================================
    const loadArmazemForEdit = async (armazem) => {
        setEditLoading(true);
        try {
            setShowDetailsModal(false);
            setArmazemDetalhes(null);

            const response = await api.get(`/armazens/${armazem._id}`);
            const armazemCompleto = response.data.data || armazem;

            const dadosMov = calcularProdutosPorArmazem([armazemCompleto]);
            let armazemComDados = dadosMov && dadosMov[0] ? dadosMov[0] : armazemCompleto;

            if (armazemComDados && armazemComDados.totalProdutos === 0) {
                const dadosFallback = calcularProdutosPorArmazemFallback([armazemCompleto], produtos);
                armazemComDados = dadosFallback && dadosFallback[0] ? dadosFallback[0] : armazemComDados;
            }

            setEditFormData({
                _id: armazemCompleto._id,
                nome: armazemCompleto.nome || '',
                codigo: armazemCompleto.codigo || '',
                tipo: armazemCompleto.tipo || 'principal',
                endereco: armazemCompleto.endereco || '',
                cidade: armazemCompleto.cidade || '',
                provincia: armazemCompleto.provincia || '',
                pais: armazemCompleto.pais || 'Moçambique',
                capacidade: armazemCompleto.capacidade || 0,
                temperatura: armazemCompleto.temperatura || '',
                umidade: armazemCompleto.umidade || '',
                responsavel: armazemCompleto.responsavel || '',
                telefone: armazemCompleto.telefone || '',
                email: armazemCompleto.email || '',
                observacoes: armazemCompleto.observacoes || '',
                status: armazemCompleto.status || 'ativo',
                ativo: armazemCompleto.ativo !== undefined ? armazemCompleto.ativo : true,
                stockTotal: armazemComDados.stockTotal || 0,
                ocupacao: armazemComDados.ocupacao || 0
            });

            setEditActiveTab('geral');

            setTimeout(() => {
                setShowEditModal(true);
            }, 100);

        } catch (error) {
            console.error('Erro ao carregar armazém:', error);
            setEditMessage({ text: 'Erro ao carregar dados do armazém', type: 'danger' });
        } finally {
            setEditLoading(false);
        }
    };

    const handleEdit = (armazem) => {
        loadArmazemForEdit(armazem);
    };

    // ==============================================
    // EDIÇÃO
    // ==============================================
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        setEditMessage({ text: '', type: '' });

        if (!editFormData.nome.trim()) {
            setEditMessage({ text: 'O nome do armazém é obrigatório', type: 'danger' });
            setEditLoading(false);
            return;
        }

        try {
            const { _id, stockTotal, ocupacao, ...updateData } = editFormData;

            const response = await api.put(`/armazens/${_id}`, updateData);

            if (response.data) {
                setEditMessage({ text: 'Armazém atualizado com sucesso!', type: 'success' });
                setTimeout(() => {
                    setShowEditModal(false);
                    resetEditForm();
                    refreshArmazens();
                }, 1500);
            }
        } catch (err) {
            console.error('❌ Erro ao atualizar armazém:', err);
            setEditMessage({
                text: err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao atualizar armazém',
                type: 'danger'
            });
        } finally {
            setEditLoading(false);
        }
    };

    // ==============================================
    // 🔥 TRANSFERÊNCIA DE STOCK ENTRE ARMAZÉNS
    // ==============================================
    const abrirMovimentacao = (armazem) => {
        const outrosArmazens = armazens.filter(a => a._id !== armazem._id && a.status === 'ativo');
        setArmazensDisponiveis(outrosArmazens);
        setArmazemMovimentacao(armazem);
        setMovimentacaoFormData({
            produtoId: '',
            quantidade: 0,
            tipoTransferencia: 'mover',
            armazemDestinoId: '',
            observacao: '',
            dataMovimentacao: obterDataHoraLocal()
        });
        setProdutoSelecionado(null);
        setMovimentacaoMessage({ text: '', type: '' });
        setMovimentacaoActiveTab('entrada');
        setShowMovimentacaoModal(true);
    };

    // ==============================================
    // 🔥 HANDLE MOVIMENTAÇÃO SUBMIT - CORRIGIDO
    // ==============================================
    const handleMovimentacaoSubmit = async (e) => {
        e.preventDefault();
        setMovimentacaoLoading(true);
        setMovimentacaoMessage({ text: '', type: '' });

        if (!movimentacaoFormData.produtoId) {
            setMovimentacaoMessage({ text: 'Selecione um produto', type: 'danger' });
            setMovimentacaoLoading(false);
            return;
        }

        const produto = produtos.find(p => p._id === movimentacaoFormData.produtoId);
        const stockDisponivel = produto?.stockAtual || 0;

        if (movimentacaoFormData.quantidade <= 0) {
            setMovimentacaoMessage({ text: 'Quantidade deve ser maior que zero', type: 'danger' });
            setMovimentacaoLoading(false);
            return;
        }

        if (movimentacaoFormData.quantidade > stockDisponivel) {
            setMovimentacaoMessage({
                text: `Quantidade (${movimentacaoFormData.quantidade}) excede o stock disponível (${stockDisponivel})`,
                type: 'danger'
            });
            setMovimentacaoLoading(false);
            return;
        }

        if (!movimentacaoFormData.armazemDestinoId) {
            setMovimentacaoMessage({ text: 'Selecione o armazém de destino', type: 'danger' });
            setMovimentacaoLoading(false);
            return;
        }

        try {
            const response = await api.post(
                `/armazens/${armazemMovimentacao._id}/transferir`,
                {
                    produtoId: movimentacaoFormData.produtoId,
                    quantidade: movimentacaoFormData.quantidade,
                    armazemDestinoId: movimentacaoFormData.armazemDestinoId,
                    tipoTransferencia: movimentacaoFormData.tipoTransferencia,
                    observacao: movimentacaoFormData.observacao,
                    dataMovimentacao: movimentacaoFormData.dataMovimentacao
                }
            );

            if (response.data) {
                const tipoLabel = movimentacaoFormData.tipoTransferencia === 'mover' ? 'Mover' : 'Duplicar/Transferir';
                setMovimentacaoMessage({
                    text: `${tipoLabel} realizado com sucesso!`,
                    type: 'success'
                });

                // 🔥 ATUALIZAR MOVIMENTAÇÕES SE O MODAL DE DETALHES ESTIVER ABERTO
                if (armazemDetalhes) {
                    await fetchMovimentacoes(armazemDetalhes._id, 1, 6);
                }

                // 🔥 RECARREGAR PRODUTOS E ARMAZÉNS
                await refreshArmazens();

                // 🔥 ATUALIZAR DETALHES DO ARMAZÉM SE O MODAL ESTIVER ABERTO
                if (armazemDetalhes) {
                    const responseDetalhes = await api.get(`/armazens/${armazemDetalhes._id}`);
                    const armazemCompleto = responseDetalhes.data.data || responseDetalhes.data;
                    const dadosMov = calcularProdutosPorArmazem([armazemCompleto]);
                    let armazemComDados = dadosMov && dadosMov[0] ? dadosMov[0] : armazemCompleto;
                    if (armazemComDados && armazemComDados.totalProdutos === 0) {
                        const dadosFallback = calcularProdutosPorArmazemFallback([armazemCompleto], produtos);
                        armazemComDados = dadosFallback && dadosFallback[0] ? dadosFallback[0] : armazemComDados;
                    }
                    setArmazemDetalhes(armazemComDados);
                }

                setTimeout(() => {
                    setShowMovimentacaoModal(false);
                    resetMovimentacaoForm();
                }, 1500);
            }
        } catch (err) {
            console.error('❌ Erro ao transferir produto:', err);
            setMovimentacaoMessage({
                text: err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao transferir produto',
                type: 'danger'
            });
        } finally {
            setMovimentacaoLoading(false);
        }
    };

    // ==============================================
    // EXCLUSÃO
    // ==============================================
    const confirmDelete = async () => {
        if (!armazemToDelete) return;

        try {
            const response = await api.delete(`/armazens/${armazemToDelete._id}`);
            if (response.data) {
                showTempToast('Sucesso', `Armazém "${armazemToDelete.nome}" removido com sucesso!`, 'success');
                closeDeleteModal();
                refreshArmazens();
            }
        } catch (err) {
            console.error('❌ Erro ao remover armazém:', err);
            showTempToast('Erro', err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao remover armazém', 'danger');
        }
    };

    // ==============================================
    // 🔥 EXPORTAÇÃO - CORRIGIDA (CSV, EXCEL E PDF)
    // ==============================================
    const exportarArmazens = async (formato) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showTempToast('Erro', 'Sessão expirada. Faça login novamente.', 'danger');
                return;
            }

            // Configurar tipo de resposta baseado no formato
            const isExcel = formato === 'excel' || formato === 'xlsx';
            const isPdf = formato === 'pdf';
            const isCsv = formato === 'csv';

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            };

            if (isExcel || isPdf) {
                config.responseType = 'blob';
            }

            const response = await api.post('/armazens/exportar', {
                formato,
                filtros: {
                    tipo: filtros.tipo !== 'todos' ? filtros.tipo : undefined,
                    status: filtros.status !== 'todos' ? filtros.status : undefined,
                    ativo: filtros.ativo !== 'todos' ? filtros.ativo : undefined
                }
            }, config);

            // 🔥 SE FOR CSV
            if (isCsv) {
                let csvContent = response.data;

                if (typeof response.data === 'object' && response.data !== null) {
                    if (response.data.data && Array.isArray(response.data.data)) {
                        const headers = Object.keys(response.data.data[0] || {});
                        let csv = headers.join(',') + '\n';
                        response.data.data.forEach(row => {
                            csv += headers.map(h => {
                                const value = row[h] !== undefined && row[h] !== null ? row[h] : '';
                                return `"${String(value).replace(/"/g, '""')}"`;
                            }).join(',') + '\n';
                        });
                        csvContent = csv;
                    } else {
                        csvContent = response.data.csv || response.data.data || JSON.stringify(response.data);
                    }
                }

                const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `armazens_${new Date().toISOString().split('T')[0]}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                showTempToast('Sucesso', 'Armazéns exportados em CSV!', 'success');
                return;
            }

            // 🔥 SE FOR PDF
            if (isPdf) {
                const blob = new Blob([response.data], {
                    type: 'application/pdf'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `armazens_${new Date().toISOString().split('T')[0]}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                showTempToast('Sucesso', 'Armazéns exportados em PDF!', 'success');
                return;
            }

            // 🔥 SE FOR EXCEL
            if (isExcel) {
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `armazens_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                showTempToast('Sucesso', 'Armazéns exportados em Excel!', 'success');
                return;
            }

            // 🔥 SE FOR JSON (fallback)
            if (response.data) {
                const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `armazens_${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                showTempToast('Sucesso', 'Armazéns exportados em JSON!', 'success');
            }
        } catch (err) {
            console.error('❌ Erro na exportação:', err);

            if (err.response && err.response.data) {
                if (err.response.data instanceof Blob) {
                    try {
                        const text = await err.response.data.text();
                        try {
                            const errorData = JSON.parse(text);
                            showTempToast('Erro', errorData.erro || errorData.message || 'Falha na exportação', 'danger');
                        } catch {
                            showTempToast('Erro', text || 'Falha na exportação', 'danger');
                        }
                    } catch {
                        showTempToast('Erro', 'Falha na exportação', 'danger');
                    }
                } else {
                    showTempToast('Erro', err.response.data?.erro || err.response.data?.message || 'Falha na exportação', 'danger');
                }
            } else {
                showTempToast('Erro', err.message || 'Falha na exportação', 'danger');
            }
        }
    };

    // ==============================================
    // RENDER PRINCIPAL
    // ==============================================
    const currentArmazens = getCurrentPageArmazens();
    const isQuantidadeDisabled = movimentacaoFormData.tipoTransferencia === 'mover';

    return (
        <div className="armazens-container">
           {/* MODAL DE CRIAÇÃO */}
{showCreateModal && (
    <div className="armazem-modal-overlay" onClick={() => { setShowCreateModal(false); resetForm(); }}>
        <div className="armazem-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="armazem-modal-header">
                <h3><Icon name="add_box" size={22} /> Novo Armazém</h3>
                <button className="armazem-modal-close" onClick={() => { setShowCreateModal(false); resetForm(); }}>×</button>
            </div>
            <div className="armazem-modal-body">
                {createMessage.text && (
                    <div className={`armazem-modal-alert armazem-modal-alert-${createMessage.type}`}>
                        <Icon name={createMessage.type === 'success' ? 'check_circle' : 'error'} size={18} />
                        <span>{createMessage.text}</span>
                        <button className="armazem-alert-close" onClick={() => setCreateMessage({ text: '', type: '' })}>×</button>
                    </div>
                )}
                <form onSubmit={handleCreateSubmit}>
                    <div className="armazem-modal-tabs">
                        <button type="button" className={`armazem-modal-tab-btn ${activeTab === 'geral' ? 'active' : ''}`} onClick={() => setActiveTab('geral')}>
                            <Icon name="info" size={16} />
                            <span className="armazem-tab-label">Dados Gerais</span>
                        </button>
                        <button type="button" className={`armazem-modal-tab-btn ${activeTab === 'localizacao' ? 'active' : ''}`} onClick={() => setActiveTab('localizacao')}>
                            <Icon name="location_on" size={16} />
                            <span className="armazem-tab-label">Localização</span>
                        </button>
                        <button type="button" className={`armazem-modal-tab-btn ${activeTab === 'capacidade' ? 'active' : ''}`} onClick={() => setActiveTab('capacidade')}>
                            <Icon name="inventory_2" size={16} />
                            <span className="armazem-tab-label">Capacidade</span>
                        </button>
                    </div>

                    {/* TAB DADOS GERAIS */}
                    <div className={`armazem-modal-tab-panel ${activeTab === 'geral' ? 'active' : ''}`}>
                        <div className="armazem-modal-section">
                            <div className="armazem-form-group">
                                <label>Nome <span className="armazem-required">*</span></label>
                                <input type="text" name="nome" value={formData.nome} onChange={handleChange} required placeholder="Nome do armazém" />
                            </div>
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Código</label>
                                    <input type="text" name="codigo" value={formData.codigo} onChange={handleChange} placeholder="Código (opcional)" />
                                </div>
                                <div className="armazem-form-group">
                                    <label>Tipo</label>
                                    <select name="tipo" value={formData.tipo} onChange={handleChange}>
                                        {TIPOS_ARMAZEM.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Responsável</label>
                                    <input type="text" name="responsavel" value={formData.responsavel} onChange={handleChange} placeholder="Nome do responsável" />
                                </div>
                                <div className="armazem-form-group">
                                    <label>Status</label>
                                    <select name="status" value={formData.status} onChange={handleChange}>
                                        {STATUS_ARMAZEM.map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Telefone</label>
                                    <input type="tel" name="telefone" value={formData.telefone} onChange={handleChange} placeholder="Telefone de contacto" />
                                </div>
                                <div className="armazem-form-group">
                                    <label>Email</label>
                                    <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email de contacto" />
                                </div>
                            </div>
                            <div className="armazem-form-group">
                                <label>Observações</label>
                                <textarea name="observacoes" value={formData.observacoes} onChange={handleChange} placeholder="Observações adicionais" rows="2" />
                            </div>
                            <div className="armazem-form-group">
                                <label className="armazem-checkbox-label">
                                    <input type="checkbox" name="ativo" checked={formData.ativo} onChange={handleChange} />
                                    Armazém Ativo
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* TAB LOCALIZAÇÃO */}
                    <div className={`armazem-modal-tab-panel ${activeTab === 'localizacao' ? 'active' : ''}`}>
                        <div className="armazem-modal-section">
                            <div className="armazem-form-group">
                                <label>Endereço</label>
                                <input type="text" name="endereco" value={formData.endereco} onChange={handleChange} placeholder="Endereço completo" />
                            </div>
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Cidade</label>
                                    <input type="text" name="cidade" value={formData.cidade} onChange={handleChange} placeholder="Cidade" />
                                </div>
                                <div className="armazem-form-group">
                                    <label>Província</label>
                                    <input type="text" name="provincia" value={formData.provincia} onChange={handleChange} placeholder="Província" />
                                </div>
                            </div>
                            <div className="armazem-form-group">
                                <label>País</label>
                                <input type="text" name="pais" value={formData.pais} onChange={handleChange} placeholder="País" />
                            </div>
                        </div>
                    </div>

                    {/* TAB CAPACIDADE */}
                    <div className={`armazem-modal-tab-panel ${activeTab === 'capacidade' ? 'active' : ''}`}>
                        <div className="armazem-modal-section">
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Capacidade Total</label>
                                    <input type="number" name="capacidade" value={formData.capacidade} onChange={handleChange} placeholder="0" min="0" />
                                    <small className="armazem-form-help">Capacidade máxima em unidades</small>
                                </div>
                                <div className="armazem-form-group">
                                    <label>Temperatura (°C)</label>
                                    <input type="number" name="temperatura" value={formData.temperatura} onChange={handleChange} placeholder="Ex: 22" step="0.1" />
                                </div>
                            </div>
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Umidade (%)</label>
                                    <input type="number" name="umidade" value={formData.umidade} onChange={handleChange} placeholder="Ex: 60" step="0.1" min="0" max="100" />
                                </div>
                                <div className="armazem-form-group">
                                    <label>&nbsp;</label>
                                    <div className="armazem-info-text">
                                        <Icon name="info" size={14} /> A taxa de ocupação é calculada automaticamente
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="armazem-modal-actions">
                        <button type="submit" className="armazem-modal-btn-primary" disabled={createLoading}>
                            {createLoading ? 'A criar...' : 'Criar Armazém'}
                        </button>
                        <button type="button" className="armazem-modal-btn-secondary" onClick={resetForm}>Limpar</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
)}

{/* MODAL DE EDIÇÃO */}
{showEditModal && editFormData && (
    <div className="armazem-modal-overlay" onClick={() => { setShowEditModal(false); resetEditForm(); }}>
        <div className="armazem-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="armazem-modal-header">
                <h3><Icon name="edit" size={22} /> Editar Armazém</h3>
                <button className="armazem-modal-close" onClick={() => { setShowEditModal(false); resetEditForm(); }}>×</button>
            </div>
            <div className="armazem-modal-body">
                {editMessage.text && (
                    <div className={`armazem-modal-alert armazem-modal-alert-${editMessage.type}`}>
                        <Icon name={editMessage.type === 'success' ? 'check_circle' : 'error'} size={18} />
                        <span>{editMessage.text}</span>
                        <button className="armazem-alert-close" onClick={() => setEditMessage({ text: '', type: '' })}>×</button>
                    </div>
                )}
                <form onSubmit={handleEditSubmit}>
                    <div className="armazem-modal-tabs">
                        <button type="button" className={`armazem-modal-tab-btn ${editActiveTab === 'geral' ? 'active' : ''}`} onClick={() => setEditActiveTab('geral')}>
                            <Icon name="info" size={16} />
                            <span className="armazem-tab-label">Dados Gerais</span>
                        </button>
                        <button type="button" className={`armazem-modal-tab-btn ${editActiveTab === 'localizacao' ? 'active' : ''}`} onClick={() => setEditActiveTab('localizacao')}>
                            <Icon name="location_on" size={16} />
                            <span className="armazem-tab-label">Localização</span>
                        </button>
                        <button type="button" className={`armazem-modal-tab-btn ${editActiveTab === 'capacidade' ? 'active' : ''}`} onClick={() => setEditActiveTab('capacidade')}>
                            <Icon name="inventory_2" size={16} />
                            <span className="armazem-tab-label">Capacidade</span>
                        </button>
                    </div>

                    <div className={`armazem-modal-tab-panel ${editActiveTab === 'geral' ? 'active' : ''}`}>
                        <div className="armazem-modal-section">
                            <div className="armazem-form-group">
                                <label>Nome <span className="armazem-required">*</span></label>
                                <input type="text" name="nome" value={editFormData.nome} onChange={handleEditChange} required placeholder="Nome do armazém" />
                            </div>
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Código</label>
                                    <input type="text" name="codigo" value={editFormData.codigo} onChange={handleEditChange} placeholder="Código" disabled />
                                </div>
                                <div className="armazem-form-group">
                                    <label>Tipo</label>
                                    <select name="tipo" value={editFormData.tipo} onChange={handleEditChange}>
                                        {TIPOS_ARMAZEM.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Responsável</label>
                                    <input type="text" name="responsavel" value={editFormData.responsavel} onChange={handleEditChange} placeholder="Nome do responsável" />
                                </div>
                                <div className="armazem-form-group">
                                    <label>Status</label>
                                    <select name="status" value={editFormData.status} onChange={handleEditChange}>
                                        {STATUS_ARMAZEM.map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Telefone</label>
                                    <input type="tel" name="telefone" value={editFormData.telefone} onChange={handleEditChange} placeholder="Telefone de contacto" />
                                </div>
                                <div className="armazem-form-group">
                                    <label>Email</label>
                                    <input type="email" name="email" value={editFormData.email} onChange={handleEditChange} placeholder="Email de contacto" />
                                </div>
                            </div>
                            <div className="armazem-form-group">
                                <label>Observações</label>
                                <textarea name="observacoes" value={editFormData.observacoes} onChange={handleEditChange} placeholder="Observações adicionais" rows="2" />
                            </div>
                            <div className="armazem-form-group">
                                <label className="armazem-checkbox-label">
                                    <input type="checkbox" name="ativo" checked={editFormData.ativo} onChange={handleEditChange} />
                                    Armazém Ativo
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className={`armazem-modal-tab-panel ${editActiveTab === 'localizacao' ? 'active' : ''}`}>
                        <div className="armazem-modal-section">
                            <div className="armazem-form-group">
                                <label>Endereço</label>
                                <input type="text" name="endereco" value={editFormData.endereco} onChange={handleEditChange} placeholder="Endereço completo" />
                            </div>
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Cidade</label>
                                    <input type="text" name="cidade" value={editFormData.cidade} onChange={handleEditChange} placeholder="Cidade" />
                                </div>
                                <div className="armazem-form-group">
                                    <label>Província</label>
                                    <input type="text" name="provincia" value={editFormData.provincia} onChange={handleEditChange} placeholder="Província" />
                                </div>
                            </div>
                            <div className="armazem-form-group">
                                <label>País</label>
                                <input type="text" name="pais" value={editFormData.pais} onChange={handleEditChange} placeholder="País" />
                            </div>
                        </div>
                    </div>

                    {/* TAB CAPACIDADE */}
                    <div className={`armazem-modal-tab-panel ${editActiveTab === 'capacidade' ? 'active' : ''}`}>
                        <div className="armazem-modal-section">
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Capacidade Total</label>
                                    <input type="number" name="capacidade" value={editFormData.capacidade} onChange={handleEditChange} placeholder="0" min="0" />
                                    <small className="armazem-form-help">Capacidade máxima em unidades</small>
                                </div>
                                <div className="armazem-form-group">
                                    <label>Temperatura (°C)</label>
                                    <input type="number" name="temperatura" value={editFormData.temperatura} onChange={handleEditChange} placeholder="Ex: 22" step="0.1" />
                                </div>
                            </div>
                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Umidade (%)</label>
                                    <input type="number" name="umidade" value={editFormData.umidade} onChange={handleEditChange} placeholder="Ex: 60" step="0.1" min="0" max="100" />
                                </div>
                                <div className="armazem-form-group">
                                    <label>Taxa de Ocupação</label>
                                    <div className="armazem-ocupacao-valor">
                                        {formatarPercentagem(editFormData.ocupacao || 0)}
                                    </div>
                                    <small className="armazem-form-help armazem-help-info">
                                        <Icon name="info" size={12} /> Calculada automaticamente com base no stock atual
                                    </small>
                                </div>
                            </div>
                            <div className="armazem-stock-info-edit">
                                <div className="armazem-stock-info-row">
                                    <span><strong>📦 Stock Total:</strong> {formatarNumero(editFormData.stockTotal || 0)} unidades</span>
                                    <span><strong>📊 Taxa de Ocupação:</strong> {formatarPercentagem(editFormData.ocupacao || 0)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="armazem-modal-actions">
                        <button type="submit" className="armazem-modal-btn-primary" disabled={editLoading}>
                            {editLoading ? 'A atualizar...' : 'Salvar Alterações'}
                        </button>
                        <button type="button" className="armazem-modal-btn-secondary" onClick={() => { setShowEditModal(false); resetEditForm(); }}>Cancelar</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
)}

{/* MODAL DE DETALHES */}
{showDetailsModal && armazemDetalhes && (
    <div className="armazem-modal-overlay" onClick={() => setShowDetailsModal(false)}>
        <div className="armazem-modal-content armazem-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="armazem-modal-header">
                <h3>
                    <Icon name="warehouse" size={22} />
                    {armazemDetalhes.nome}
                    {!armazemDetalhes.ativo && (
                        <span className="armazem-status-badge inactive" style={{ marginLeft: '0.5rem' }}>
                            <Icon name="block" size={12} /> Inativo
                        </span>
                    )}
                </h3>
                <button className="armazem-modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="armazem-modal-body">
                <div className="armazem-modal-tabs">
                    <button type="button" className={`armazem-modal-tab-btn ${detailsActiveTab === 'dados' ? 'active' : ''}`} onClick={() => setDetailsActiveTab('dados')}>
                        <Icon name="info" size={16} />
                        <span className="armazem-tab-label">Dados</span>
                    </button>
                    <button type="button" className={`armazem-modal-tab-btn ${detailsActiveTab === 'capacidade' ? 'active' : ''}`} onClick={() => setDetailsActiveTab('capacidade')}>
                        <Icon name="inventory_2" size={16} />
                        <span className="armazem-tab-label">Capacidade</span>
                    </button>
                    <button type="button" className={`armazem-modal-tab-btn ${detailsActiveTab === 'graficos' ? 'active' : ''}`} onClick={() => setDetailsActiveTab('graficos')}>
                        <Icon name="trending_up" size={16} />
                        <span className="armazem-tab-label">Gráficos</span>
                    </button>
                    <button type="button" className={`armazem-modal-tab-btn ${detailsActiveTab === 'historico' ? 'active' : ''}`} onClick={async () => {
                        setDetailsActiveTab('historico');
                        if (armazemDetalhes) {
                            setMovimentacoes([]);
                            await fetchMovimentacoes(armazemDetalhes._id, 1, 6);
                        }
                    }}>
                        <Icon name="history" size={16} />
                        <span className="armazem-tab-label">Histórico</span>
                    </button>
                </div>

                {/* TAB DADOS */}
                <div className={`armazem-modal-tab-panel ${detailsActiveTab === 'dados' ? 'active' : ''}`}>
                    <div className="armazem-detalhes-grid">
                        <div className="armazem-info-item">
                            <span className="armazem-label">📌 Código</span>
                            <span className="armazem-value">{armazemDetalhes.codigo || '-'}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">🏷️ Tipo</span>
                            <span className="armazem-value">{getTipoLabel(armazemDetalhes.tipo)}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">📍 Endereço</span>
                            <span className="armazem-value">{armazemDetalhes.endereco || '-'}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">🏙️ Cidade</span>
                            <span className="armazem-value">{armazemDetalhes.cidade || '-'}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">📌 Província</span>
                            <span className="armazem-value">{armazemDetalhes.provincia || '-'}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">🌍 País</span>
                            <span className="armazem-value">{armazemDetalhes.pais || '-'}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">👤 Responsável</span>
                            <span className="armazem-value">{armazemDetalhes.responsavel || '-'}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">📞 Telefone</span>
                            <span className="armazem-value">{armazemDetalhes.telefone || '-'}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">✉️ Email</span>
                            <span className="armazem-value">{armazemDetalhes.email || '-'}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">📊 Status</span>
                            <span className="armazem-value">
                                <span className={`armazem-status-badge ${armazemDetalhes.status}`}>
                                    <Icon name={getStatusArmazem(armazemDetalhes.status).icon} size={12} />
                                    {getStatusArmazem(armazemDetalhes.status).label}
                                </span>
                            </span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">🌡️ Temperatura</span>
                            <span className="armazem-value">{armazemDetalhes.temperatura ? `${armazemDetalhes.temperatura}°C` : '-'}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">💧 Umidade</span>
                            <span className="armazem-value">{armazemDetalhes.umidade ? `${armazemDetalhes.umidade}%` : '-'}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">📦 Produtos</span>
                            <span className="armazem-value">{armazemDetalhes.totalProdutos || 0}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">📊 Stock Total</span>
                            <span className="armazem-value">{formatarNumero(armazemDetalhes.stockTotal || 0)} unidades</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">📈 Taxa de Ocupação</span>
                            <span className="armazem-value">{formatarPercentagem(armazemDetalhes.ocupacao || 0)}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">📅 Data de Criação</span>
                            <span className="armazem-value">{formatarDataHora(armazemDetalhes.createdAt)}</span>
                        </div>
                        <div className="armazem-info-item">
                            <span className="armazem-label">🔄 Última Atualização</span>
                            <span className="armazem-value">{formatarDataHora(armazemDetalhes.updatedAt)}</span>
                        </div>
                    </div>
                    {armazemDetalhes.observacoes && (
                        <div className="armazem-descricao-box">
                            <h4>📝 Observações</h4>
                            <p>{armazemDetalhes.observacoes}</p>
                        </div>
                    )}
                </div>

                {/* TAB CAPACIDADE */}
                <div className={`armazem-modal-tab-panel ${detailsActiveTab === 'capacidade' ? 'active' : ''}`}>
                    <div className="armazem-capacidade-info">
                        <div className="armazem-capacidade-card">
                            <div className="armazem-capacidade-icon">
                                <Icon name="inventory_2" size={32} />
                            </div>
                            <div className="armazem-capacidade-dados">
                                <span className="armazem-capacidade-label">Capacidade Total</span>
                                <span className="armazem-capacidade-valor">{formatarNumero(armazemDetalhes.capacidade)} unidades</span>
                            </div>
                        </div>
                        <div className="armazem-capacidade-card">
                            <div className="armazem-capacidade-icon" style={{ color: '#10b981' }}>
                                <Icon name="trending_up" size={32} />
                            </div>
                            <div className="armazem-capacidade-dados">
                                <span className="armazem-capacidade-label">Taxa de Ocupação</span>
                                <span className="armazem-capacidade-valor" style={{ color: armazemDetalhes.ocupacao >= 90 ? '#dc2626' : armazemDetalhes.ocupacao >= 70 ? '#f59e0b' : '#10b981' }}>
                                    {formatarPercentagem(armazemDetalhes.ocupacao || 0)}
                                </span>
                            </div>
                        </div>
                        <div className="armazem-capacidade-card" style={{ borderColor: '#8b5cf6' }}>
                            <div className="armazem-capacidade-icon" style={{ color: '#8b5cf6' }}>
                                <Icon name="inventory" size={32} />
                            </div>
                            <div className="armazem-capacidade-dados">
                                <span className="armazem-capacidade-label">Total de Produtos</span>
                                <span className="armazem-capacidade-valor">{formatarNumero(armazemDetalhes.totalProdutos || 0)}</span>
                            </div>
                        </div>
                        <div className="armazem-capacidade-card" style={{ borderColor: '#dc2626' }}>
                            <div className="armazem-capacidade-icon" style={{ color: '#dc2626' }}>
                                <Icon name="inventory_2" size={32} />
                            </div>
                            <div className="armazem-capacidade-dados">
                                <span className="armazem-capacidade-label">Stock Total</span>
                                <span className="armazem-capacidade-valor">{formatarNumero(armazemDetalhes.stockTotal || 0)} unidades</span>
                            </div>
                        </div>
                        <div className="armazem-capacidade-card" style={{ borderColor: '#3b82f6' }}>
                            <div className="armazem-capacidade-icon" style={{ color: '#3b82f6' }}>
                                <Icon name="info" size={32} />
                            </div>
                            <div className="armazem-capacidade-dados">
                                <span className="armazem-capacidade-label">Status</span>
                                <span className="armazem-capacidade-valor">
                                    <span className={`armazem-status-badge ${getCapacidadeStatus(armazemDetalhes.ocupacao || 0).label.toLowerCase().replace(' ', '_')}`}>
                                        {getCapacidadeStatus(armazemDetalhes.ocupacao || 0).label}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TAB GRÁFICOS */}
                <div className={`armazem-modal-tab-panel ${detailsActiveTab === 'graficos' ? 'active' : ''}`}>
                    <div className="armazem-graficos-grid">
                        <div className="armazem-grafico-card">
                            <SimpleBarChart
                                title="📊 Capacidade do Armazém"
                                labels={['Capacidade Total', 'Stock Total']}
                                data={[
                                    armazemDetalhes.capacidade || 0,
                                    armazemDetalhes.stockTotal || 0
                                ]}
                                colors={['#4f46e5', '#dc2626']}
                                formatValue={formatarNumero}
                            />
                        </div>
                        <div className="armazem-grafico-card armazem-gauge-card">
                            <div className="armazem-gauge-wrapper">
                                <SimpleGauge
                                    value={armazemDetalhes.stockTotal || 0}
                                    max={armazemDetalhes.capacidade || 1}
                                    label="Taxa de Ocupação"
                                    color={
                                        armazemDetalhes.capacidade > 0
                                            ? (armazemDetalhes.ocupacao || 0) >= 90
                                                ? '#dc2626'
                                                : (armazemDetalhes.ocupacao || 0) >= 70
                                                    ? '#f59e0b'
                                                    : '#10b981'
                                            : '#6b7280'
                                    }
                                    formatValue={(v) => `${formatarPercentagem((v / armazemDetalhes.capacidade) * 100)}`}
                                />
                            </div>
                            <div className="armazem-gauge-info">
                                <div className="armazem-gauge-status">
                                    <span className="armazem-status-label">Status:</span>
                                    <span className={`armazem-status-badge ${getCapacidadeStatus(armazemDetalhes.ocupacao || 0).label.toLowerCase().replace(' ', '_')}`}>
                                        {getCapacidadeStatus(armazemDetalhes.ocupacao || 0).label}
                                    </span>
                                </div>
                                <div className="armazem-gauge-metrics">
                                    <span>📦 Produtos: {formatarNumero(armazemDetalhes.totalProdutos || 0)}</span>
                                    <span>📈 Ocupação: {formatarPercentagem(armazemDetalhes.ocupacao || 0)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* TAB HISTÓRICO */}
                <div className={`armazem-modal-tab-panel ${detailsActiveTab === 'historico' ? 'active' : ''}`}>
                    <div className="armazem-historico-section">
                        <h4 className="armazem-historico-title">
                            <Icon name="history" size={18} /> Histórico de Movimentações
                        </h4>
                        {movimentacoes && movimentacoes.length > 0 ? (
                            <>
                                <div className="armazem-historico-timeline">
                                    {movimentacoes.map((mov, index) => {
                                        const isEntrada = mov.tipo === 'entrada' || mov.tipo === 'transferencia_entrada';
                                        const isTransferencia = mov.tipo === 'transferencia_saida' || mov.tipo === 'transferencia_entrada';
                                        const cor = isEntrada ? '#10b981' : '#dc2626';
                                        const iconName = isEntrada ? 'add_box' : 'remove_circle';
                                        const sinal = isEntrada ? '+' : '-';
                                        const tipoLabel = isTransferencia ? '🔄 Transferência' : (isEntrada ? 'Entrada' : 'Saída');

                                        return (
                                            <div key={index} className={`armazem-historico-item ${mov.tipo}`}>
                                                <div className="armazem-historico-icon" style={{ background: cor }}>
                                                    <Icon name={iconName} size={14} style={{ color: 'white' }} />
                                                </div>
                                                <div className="armazem-historico-content">
                                                    <div className="armazem-historico-header">
                                                        <span className="armazem-historico-quantidade" style={{ color: cor }}>
                                                            {sinal}{formatarNumero(mov.quantidade)} unidades
                                                        </span>
                                                        <span className="armazem-historico-tipo-label">{tipoLabel}</span>
                                                        <span className="armazem-historico-data">
                                                            {formatarDataHora(mov.dataMovimentacao)}
                                                        </span>
                                                    </div>
                                                    <div className="armazem-historico-detalhes">
                                                        <span>Produto: {mov.produtoNome || 'N/A'}</span>
                                                        {mov.armazemOrigem && (
                                                            <span>📍 Origem: {mov.armazemOrigem}</span>
                                                        )}
                                                        {mov.armazemDestino && (
                                                            <span>🎯 Destino: {mov.armazemDestino}</span>
                                                        )}
                                                        {mov.precoUnitario > 0 && (
                                                            <span>Preço: {formatarMoeda(mov.precoUnitario)}</span>
                                                        )}
                                                        {mov.observacao && (
                                                            <span className="armazem-historico-observacao">{mov.observacao}</span>
                                                        )}
                                                        {mov.criadoPorNome && (
                                                            <span>Por: {mov.criadoPorNome}</span>
                                                        )}
                                                        {mov.notaFiscal && (
                                                            <span className="armazem-historico-nota">📄 NF: {mov.notaFiscal}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {renderHistoricoPagination()}
                            </>
                        ) : (
                            <div className="armazem-historico-vazio">
                                <Icon name="history" size={32} />
                                <p>Nenhuma movimentação registada para este armazém</p>
                                <button
                                    className="armazem-btn-create-small"
                                    onClick={() => {
                                        setShowDetailsModal(false);
                                        if (armazemDetalhes) {
                                            abrirMovimentacao(armazemDetalhes);
                                        }
                                    }}
                                    style={{ marginTop: '0.5rem' }}
                                >
                                    <Icon name="add" size={14} /> Registrar Movimentação
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="armazem-modal-footer">
                <button className="armazem-btn-primary" onClick={() => handleEdit(armazemDetalhes)}>
                    <Icon name="edit" size={16} /> Editar
                </button>
                <button className="armazem-btn-secondary" onClick={() => setShowDetailsModal(false)}>Fechar</button>
            </div>
        </div>
    </div>
)}

{/* 🔥 MODAL DE TRANSFERÊNCIA ENTRE ARMAZÉNS */}
{showMovimentacaoModal && armazemMovimentacao && (
    <div className="armazem-modal-overlay" onClick={() => { setShowMovimentacaoModal(false); resetMovimentacaoForm(); }}>
        <div className="armazem-modal-content armazem-modal-transferencia" onClick={(e) => e.stopPropagation()}>
            <div className="armazem-modal-header">
                <h3><Icon name="swap_horiz" size={22} /> Transferir Produto</h3>
                <button className="armazem-modal-close" onClick={() => { setShowMovimentacaoModal(false); resetMovimentacaoForm(); }}>×</button>
            </div>
            <div className="armazem-modal-body">
                <div className="armazem-movimentacao-info">
                    <div className="armazem-info-grid">
                        <div className="armazem-info-item-dest">
                            <span className="armazem-label">📦 Origem</span>
                            <span className="armazem-value"><strong>{armazemMovimentacao.nome}</strong></span>
                            <span className="armazem-sub-info">Stock: {formatarNumero(armazemMovimentacao.stockTotal || 0)} un. | Ocupação: {formatarPercentagem(armazemMovimentacao.ocupacao || 0)}</span>
                        </div>
                        <div className="armazem-info-arrow">
                            <Icon name="arrow_forward" size={32} style={{ color: '#4f46e5' }} />
                        </div>
                        <div className="armazem-info-item-dest">
                            <span className="armazem-label">🎯 Destino</span>
                            <span className="armazem-value" style={{ color: movimentacaoFormData.armazemDestinoId ? '#059669' : '#6b7280' }}>
                                {movimentacaoFormData.armazemDestinoId
                                    ? armazens.find(a => a._id === movimentacaoFormData.armazemDestinoId)?.nome || 'Selecionado'
                                    : 'Aguardando seleção...'}
                            </span>
                        </div>
                    </div>
                </div>

                {movimentacaoMessage.text && (
                    <div className={`armazem-modal-alert armazem-modal-alert-${movimentacaoMessage.type}`}>
                        <Icon name={movimentacaoMessage.type === 'success' ? 'check_circle' : 'error'} size={18} />
                        <span>{movimentacaoMessage.text}</span>
                        <button className="armazem-alert-close" onClick={() => setMovimentacaoMessage({ text: '', type: '' })}>×</button>
                    </div>
                )}

                <form onSubmit={handleMovimentacaoSubmit}>
                    <div className="armazem-modal-tabs">
                        <button type="button" className={`armazem-modal-tab-btn ${movimentacaoActiveTab === 'entrada' ? 'active' : ''}`} onClick={() => setMovimentacaoActiveTab('entrada')}>
                            <Icon name="settings" size={16} />
                            <span className="armazem-tab-label">Configuração</span>
                        </button>
                    </div>

                    <div className={`armazem-modal-tab-panel ${movimentacaoActiveTab === 'entrada' ? 'active' : ''}`}>
                        <div className="armazem-modal-section">
                            <div className="armazem-form-group">
                                <label>Produto <span className="armazem-required">*</span></label>
                                <select
                                    name="produtoId"
                                    value={movimentacaoFormData.produtoId}
                                    onChange={handleMovimentacaoChange}
                                    required
                                >
                                    <option value="">Selecione um produto</option>
                                    {produtos
                                        .filter(p => {
                                            const armazemId = typeof p.armazemId === 'object' ? p.armazemId?._id : p.armazemId;
                                            return armazemId === armazemMovimentacao._id && (p.stockAtual || 0) > 0;
                                        })
                                        .map(p => (
                                            <option key={p._id} value={p._id}>
                                                {p.nome} - Stock: {formatarNumero(p.stockAtual)} unidades
                                            </option>
                                        ))
                                    }
                                </select>
                                <small className="armazem-form-help">Apenas produtos com stock disponível no armazém de origem</small>
                            </div>

                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Quantidade <span className="armazem-required">*</span></label>
                                    <input
                                        type="number"
                                        name="quantidade"
                                        value={movimentacaoFormData.quantidade}
                                        onChange={handleMovimentacaoChange}
                                        required
                                        min="1"
                                        step="1"
                                        placeholder="0"
                                        disabled={isQuantidadeDisabled}
                                        
                                    />
                                    <small className="armazem-form-help">
                                        {isQuantidadeDisabled
                                            ? `🔒 A quantidade é automática (${formatarNumero(movimentacaoFormData.quantidade)} unidades) para mover todo o stock`
                                            : 'Quantidade a transferir'}
                                    </small>
                                </div>
                                <div className="armazem-form-group">
                                    <label>Data da Transferência <span className="armazem-required">*</span></label>
                                    <input
                                        type="datetime-local"
                                        name="dataMovimentacao"
                                        value={movimentacaoFormData.dataMovimentacao}
                                        readOnly
                                       
                                    />
                                    <small className="armazem-form-help">🔒 Data e hora automáticas (não editável)</small>
                                </div>
                            </div>

                            <div className="armazem-form-row">
                                <div className="armazem-form-group">
                                    <label>Tipo de Transferência <span className="armazem-required">*</span></label>
                                    <select
                                        name="tipoTransferencia"
                                        value={movimentacaoFormData.tipoTransferencia}
                                        onChange={handleMovimentacaoChange}
                                    >
                                        {TIPO_TRANSFERENCIA.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                    <small className="armazem-form-help">
                                        {movimentacaoFormData.tipoTransferencia === 'mover'
                                            ? '🔄 Remove o produto do armazém de origem (quantidade = stock total)'
                                            : '📋 Mantém o produto no armazém de origem e cria cópia no destino'}
                                    </small>
                                </div>
                                <div className="armazem-form-group">
                                    <label>Armazém Destino <span className="armazem-required">*</span></label>
                                    <select
                                        name="armazemDestinoId"
                                        value={movimentacaoFormData.armazemDestinoId}
                                        onChange={handleMovimentacaoChange}
                                        required
                                    >
                                        <option value="">Selecione o armazém de destino</option>
                                        {armazensDisponiveis.map(a => (
                                            <option key={a._id} value={a._id}>
                                                {a.nome} - {getTipoLabel(a.tipo)} ({formatarNumero(a.stockTotal || 0)} un. | {formatarPercentagem(a.ocupacao || 0)})
                                            </option>
                                        ))}
                                    </select>
                                    {armazensDisponiveis.length === 0 && (
                                        <small className="armazem-form-help armazem-help-danger">
                                            <Icon name="warning" size={12} /> Não há outros armazéns ativos disponíveis
                                        </small>
                                    )}
                                </div>
                            </div>

                            <div className="armazem-form-group">
                                <label>Observação</label>
                                <textarea
                                    name="observacao"
                                    value={movimentacaoFormData.observacao}
                                    onChange={handleMovimentacaoChange}
                                    placeholder="Motivo da transferência"
                                    rows="2"
                                />
                            </div>

                            {/* Resumo da transferência */}
                            {movimentacaoFormData.produtoId && movimentacaoFormData.quantidade > 0 && movimentacaoFormData.armazemDestinoId && (
                                <div className="armazem-transferencia-resumo">
                                    <h4 className="armazem-resumo-title">
                                        <Icon name="check_circle" size={16} /> Resumo da Transferência
                                    </h4>
                                    <div className="armazem-resumo-grid">
                                        <div><strong>Produto:</strong> {produtos.find(p => p._id === movimentacaoFormData.produtoId)?.nome || 'N/A'}</div>
                                        <div><strong>Quantidade:</strong> {formatarNumero(movimentacaoFormData.quantidade)} unidades</div>
                                        <div><strong>Origem:</strong> {armazemMovimentacao.nome}</div>
                                        <div><strong>Destino:</strong> {armazens.find(a => a._id === movimentacaoFormData.armazemDestinoId)?.nome || 'N/A'}</div>
                                        <div className="armazem-resumo-full">
                                            <strong>Tipo:</strong> {movimentacaoFormData.tipoTransferencia === 'mover' ? '🔄 Mover (remove do origem)' : '📋 Duplicar/Transferir (mantém no origem)'}
                                        </div>
                                        {isQuantidadeDisabled && (
                                            <div className="armazem-resumo-full armazem-resumo-info">
                                                <Icon name="info" size={14} /> Todo o stock do produto será movido para o destino
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="armazem-modal-actions">
                        <button type="submit" className="armazem-modal-btn-primary" disabled={movimentacaoLoading || armazensDisponiveis.length === 0}>
                            {movimentacaoLoading ? 'A processar...' : 'Transferir Produto'}
                        </button>
                        <button type="button" className="armazem-modal-btn-secondary" onClick={() => { setShowMovimentacaoModal(false); resetMovimentacaoForm(); }}>Cancelar</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
)}

{/* 🔥 MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
{showDeleteModal && armazemToDelete && (
    <div className="armazem-modal-overlay" onClick={closeDeleteModal}>
        <div className="armazem-modal-content-popups armazem-modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="armazem-modal-header">
                <h3><span className="armazem-header-icon">⚠️</span> Confirmar Exclusão</h3>
                <button className="armazem-modal-close" onClick={closeDeleteModal}>×</button>
            </div>
            <div className="armazem-modal-body-exc">
                <div className="armazem-confirm-icon warning">
                    <Icon name="warning" size={32} />
                </div>
                <h4>Remover armazém?</h4>
                <p>Tem certeza que deseja remover o armazém <strong>{armazemToDelete.nome}</strong>?</p>
                <p className="armazem-confirm-subtext"><Icon name="info" size={14} /> Esta ação é irreversível.</p>
            </div>
            <div className="armazem-modal-actions">
                <button className="armazem-btn-confirm-danger" onClick={confirmDelete}>
                    <Icon name="delete_forever" size={16} /> Eliminar
                </button>
                <button className="armazem-btn-cancel" onClick={closeDeleteModal}>Cancelar</button>
            </div>
        </div>
    </div>
)}

            {/* HEADER */}
            <div className="armazens-header">
                <div className="header-title">
                    <h2><Icon name="warehouse" size={24} /> Gestão de Armazéns</h2>
                    <p>Gerencie os armazéns e o stock da sua empresa</p>
                </div>
                <div className="header-actions">
                    <button className="btn-export" onClick={() => exportarArmazens('pdf')} title="Exportar PDF">
                        <Icon name="picture_as_pdf" size={18} /> PDF
                    </button>
                    <button className="btn-export" onClick={() => exportarArmazens('excel')} title="Exportar Excel">
                        <Icon name="file_download" size={18} /> Excel
                    </button>
                    <button className="btn-export" onClick={() => exportarArmazens('csv')} title="Exportar CSV">
                        <Icon name="file_download" size={18} /> CSV
                    </button>
                    <button className="btn-create" onClick={() => setShowCreateModal(true)}>
                        <Icon name="add" size={18} /> Novo Armazém
                    </button>
                </div>
            </div>

            {/* MENSAGENS */}
            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    <Icon name={message.type === 'success' ? 'check_circle' : 'error'} size={16} />
                    <span>{message.text}</span>
                    <button className="alert-close" onClick={() => setMessage({ text: '', type: '' })}>×</button>
                </div>
            )}

            {/* KPIs */}
            <div className="armazens-kpi-grid">
                <div className="kpi-card primary">
                    <div className="kpi-icon"><Icon name="warehouse" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{formatarNumero(stats.total)}</span>
                        <span className="kpi-label">Total Armazéns</span>
                    </div>
                </div>
                <div className="kpi-card success">
                    <div className="kpi-icon"><Icon name="check_circle" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{formatarNumero(stats.ativos)}</span>
                        <span className="kpi-label">Ativos</span>
                    </div>
                    <span className="kpi-change positive">{stats.total > 0 ? Math.round((stats.ativos / stats.total) * 100) : 0}%</span>
                </div>
                <div className="kpi-card warning">
                    <div className="kpi-icon"><Icon name="build" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{formatarNumero(stats.manutencao)}</span>
                        <span className="kpi-label">Em Manutenção</span>
                    </div>
                </div>
                <div className="kpi-card info">
                    <div className="kpi-icon"><Icon name="inventory_2" /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{formatarPercentagem(stats.ocupacaoTotal)}</span>
                        <span className="kpi-label">Taxa de Ocupação</span>
                    </div>
                </div>
            </div>

            {/* FILTROS */}
            <div className="armazens-filters">
                <div className="search-box">
                    <Icon name="search" size={18} />
                    <input type="text" placeholder="Pesquisar por nome, código, endereço, cidade ou responsável..." value={searchTerm || ''} onChange={(e) => setSearchTerm(e.target.value)} />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => setSearchTerm('')}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
                <div className="filter-actions">
                    <button className="btn-filter" onClick={() => setShowFiltros(!showFiltros)}>
                        <Icon name="filter_list" size={18} />
                        {showFiltros ? 'Ocultar Filtros' : 'Filtros'}
                    </button>
                    {/* 🔥 BOTÃO DE REFRESH - USA A MESMA FUNÇÃO */}
                    <button className="btn-refresh" onClick={refreshArmazens} title="Atualizar">
                        <Icon name="refresh" size={18} />
                    </button>
                </div>
            </div>

            {/* FILTROS AVANÇADOS */}
            {showFiltros && (
                <div className="armazens-filtros-avancados">
                    <div className="filtros-row">
                        <div className="filtro-group">
                            <label>Tipo</label>
                            <select value={filtros.tipo} onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}>
                                <option value="todos">Todos os Tipos</option>
                                {TIPOS_ARMAZEM.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Status</label>
                            <select value={filtros.status} onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}>
                                <option value="todos">Todos os Status</option>
                                {STATUS_ARMAZEM.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Ocupação</label>
                            <select value={filtros.capacidade} onChange={(e) => setFiltros({ ...filtros, capacidade: e.target.value })}>
                                <option value="todos">Todas</option>
                                <option value="cheio">🔴 Lotado (≥90%)</option>
                                <option value="quase_cheio">🟡 Quase Cheio (70-89%)</option>
                                <option value="moderado">🔵 Moderado (40-69%)</option>
                                <option value="livre">🟢 Livre (&lt;40%)</option>
                            </select>
                        </div>
                        <button className="btn-aplicar-filtros" onClick={() => {
                            let resultados = [...armazens].filter(a => a !== null);
                            // ... (lógica de filtros)
                            setFilteredArmazens(resultados);
                        }}>
                            <Icon name="filter_list" size={16} /> Aplicar
                        </button>
                        <button className="btn-limpar-filtros" onClick={limparFiltros}>
                            <Icon name="clear" size={16} /> Limpar
                        </button>
                    </div>
                </div>
            )}

            {/* TABELA */}
            {loading ? (
                <div className="loading-container">
                    <div className="spinner-large"></div>
                    <p>A carregar armazéns...</p>
                </div>
            ) : (
                <div className="armazens-table-container">
                    <table className="armazens-table">
                        <thead>
                            <tr>
                                <th className="col-data">Data/Hora</th>
                                <th className="col-codigo">Código</th>
                                <th className="col-nome">Nome</th>
                                <th className="col-tipo">Tipo</th>
                                <th className="col-localizacao">Localização</th>
                                <th className="col-capacidade">Capacidade</th>
                                <th className="col-ocupacao">Ocupação</th>
                                <th className="col-produtos">Produtos</th>
                                <th className="col-status">Status</th>
                                <th className="col-acoes">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentArmazens.length === 0 ? (
                                <tr className="empty-row">
                                    <td colSpan="10">
                                        <div className="empty-state">
                                            <Icon name="warehouse" size={48} />
                                            <p>Nenhum armazém encontrado</p>
                                            <button className="btn-create-small" onClick={() => setShowCreateModal(true)}>
                                                Criar primeiro armazém
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                currentArmazens.map(armazem => {
                                    if (!armazem) return null;

                                    const status = getStatusArmazem(armazem.status);
                                    const isAtivo = armazem.ativo !== false;

                                    const ocupacaoPercent = armazem.ocupacao !== undefined
                                        ? armazem.ocupacao
                                        : (armazem.capacidade > 0
                                            ? (armazem.stockTotal / armazem.capacidade) * 100
                                            : 0);

                                    return (
                                        <tr key={armazem._id} className={!isAtivo ? 'inactive-row' : ''}>
                                            <td className="col-data">
                                                <span className="data-hora-badge">
                                                    <span className="data">{formatarDataHora(armazem.createdAt).split(' ')[0]}</span>
                                                    <span className="hora">{formatarDataHora(armazem.createdAt).split(' ')[1]}</span>
                                                </span>
                                            </td>
                                            <td><span className="codigo-badge">{armazem.codigo || '-'}</span></td>
                                            <td>
                                                <div className="armazem-info">
                                                    <strong>{armazem.nome}</strong>
                                                    {!isAtivo && (
                                                        <span className="status-badge inactive" style={{ fontSize: '0.5rem', padding: '0.1rem 0.4rem' }}>
                                                            Inativo
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td><span className="tipo-badge">{getTipoLabel(armazem.tipo)}</span></td>
                                            <td>
                                                <div className="localizacao-info">
                                                    <span>{armazem.cidade || '-'}</span>
                                                    <span className="provincia">{armazem.provincia || ''}</span>
                                                </div>
                                            </td>
                                            <td>{formatarNumero(armazem.capacidade)}</td>
                                            <td>
                                                <div className="ocupacao-bar">
                                                    <div className="ocupacao-track">
                                                        <div
                                                            className="ocupacao-fill"
                                                            style={{
                                                                width: `${Math.min(ocupacaoPercent, 100)}%`,
                                                                backgroundColor: ocupacaoPercent >= 90 ? '#dc2626' : ocupacaoPercent >= 70 ? '#f59e0b' : '#10b981'
                                                            }}
                                                        />
                                                    </div>
                                                    <span className="ocupacao-text">{formatarPercentagem(ocupacaoPercent)}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="produtos-info">
                                                    <span className="produtos-count">
                                                        <Icon name="inventory_2" size={14} />
                                                        {armazem?.totalProdutos || 0} produtos
                                                    </span>
                                                    <span className="produtos-stock">
                                                        Stock: {formatarNumero(armazem?.stockTotal || 0)} un.
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`status-badge ${armazem.status}`}>
                                                    <Icon name={status.icon} size={12} />
                                                    {status.label}
                                                </span>
                                            </td>
                                            <td className="actions-cell">
                                                <button className="action-btn view" onClick={() => verDetalhes(armazem)} title="Ver detalhes">
                                                    <Icon name="visibility" size={16} />
                                                </button>
                                                <button className="action-btn edit" onClick={() => handleEdit(armazem)} title="Editar">
                                                    <Icon name="edit" size={16} />
                                                </button>
                                                <button className="action-btn move" onClick={() => abrirMovimentacao(armazem)} title="Transferir produto">
                                                    <Icon name="swap_horiz" size={16} />
                                                </button>
                                                <button className="action-btn delete" onClick={() => openDeleteModal(armazem)} title="Excluir">
                                                    <Icon name="delete" size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                    {renderPagination()}
                </div>
            )}
        </div>
    );
}